'use client';

import React, { useState, useRef } from 'react';

interface LiquidGlassCardProps {
  children: React.ReactNode;
  className?: string;
  isFeatured?: boolean;
  cornerCrosshairs?: boolean;
}

/**
 * Liquid Glass Card with Tactile Brutalism & Specular Refraction (Trend 2026)
 * - Адаптивное преломление света (Liquid Glass)
 * - Осязаемый брутализм: 1px рамка, острые углы (0px / 2px), угловые метки швейцарских часов
 * - Интерактивный блик толстого оптического стекла при перемещении курсора
 */
export const LiquidGlassCard: React.FC<LiquidGlassCardProps> = ({
  children,
  className = '',
  isFeatured = false,
  cornerCrosshairs = true,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0, opacity: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      opacity: 1,
    });
  };

  const handleMouseLeave = () => {
    setMousePos((prev) => ({ ...prev, opacity: 0 }));
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative overflow-hidden transition-all duration-300 ${
        isFeatured 
          ? 'bg-[#FFFFFF]/90 backdrop-blur-xl border border-[#8C6D37]/50 shadow-[0_12px_32px_-4px_rgba(140,109,55,0.12)]' 
          : 'bg-[#FFFFFF]/80 backdrop-blur-lg border border-[#EAE5DE] shadow-[0_4px_20px_-2px_rgba(45,35,20,0.03)]'
      } rounded-[2px] ${className}`}
    >
      {/* Dynamic Liquid Glass Specular Spotlight (Refraction) */}
      <div
        className="pointer-events-none absolute -inset-px transition-opacity duration-300"
        style={{
          opacity: mousePos.opacity,
          background: `radial-gradient(400px circle at ${mousePos.x}px ${mousePos.y}px, rgba(140, 109, 55, 0.08), transparent 70%)`,
        }}
      />

      {/* Top Hairline Prism Highlight (Хрустальная фаска) */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/80 to-transparent pointer-events-none" />

      {/* Tactile Brutalism Corner Crosshairs (Швейцарские метки корпуса) */}
      {cornerCrosshairs && (
        <>
          <span className="absolute top-1.5 left-1.5 font-mono text-[9px] text-[#8C6D37]/40 leading-none select-none pointer-events-none">+</span>
          <span className="absolute top-1.5 right-1.5 font-mono text-[9px] text-[#8C6D37]/40 leading-none select-none pointer-events-none">+</span>
          <span className="absolute bottom-1.5 left-1.5 font-mono text-[9px] text-[#8C6D37]/40 leading-none select-none pointer-events-none">+</span>
          <span className="absolute bottom-1.5 right-1.5 font-mono text-[9px] text-[#8C6D37]/40 leading-none select-none pointer-events-none">+</span>
        </>
      )}

      {/* Card Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};
